import React, { useState } from "react";
import IAddress from "../../../models/IAddress";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "@services/RestService";
import OwButton from "../OwButton/OwButton";
import OwTitle from "../OwTitle/OwTitle";
import DeliveryWindowsSection, { DeliveryWindowsData } from "./DeliveryWindowsSection";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    address: IAddress;
    closeModalFn: () => void;
    onSaveSuccess: (updatedAddress: IAddress) => void;
};

export const DeliveryWindowsModal: React.FC<Props> = (props) => {
    const { address, closeModalFn, onSaveSuccess } = props;
    const { supplierModel, userModel, vendorModel, uiContext } = useAppContextNonNull();

    // DeliveryWindowsSection reads has_delivery_window from the address it's given,
    // so changes must merge back into a working copy for the toggle/section to respond.
    const [workingAddress, setWorkingAddress] = useState<IAddress>({ ...address });
    const [isSaving, setIsSaving] = useState(false);

    const handleDeliveryWindowsChange = (data: DeliveryWindowsData) => {
        setWorkingAddress(
            (prev) =>
                ({
                    ...prev,
                    ...data,
                }) as IAddress
        );
    };

    const handleSave = async () => {
        if (!address.id) {
            // Unsaved address (e.g. an order ship-to typed on the fly): hand the delivery
            // window data back to the caller to persist through its own save flow.
            onSaveSuccess(workingAddress);
            closeModalFn();
            return;
        }

        setIsSaving(true);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.AddressDefaultDeliveryWindowUpdate,
                params: { addressId: String(address.id) },
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel: userModel.isSupplierUser ? undefined : vendorModel,
            });

            const deliveryWindowData: DeliveryWindowsData = {
                has_delivery_window: workingAddress.has_delivery_window,
                delivery_window_start: workingAddress.delivery_window_start,
                delivery_window_end: workingAddress.delivery_window_end,
                delivery_window_is_hard: workingAddress.delivery_window_is_hard,
                delivery_window_soft_penalty_per_hour: workingAddress.delivery_window_soft_penalty_per_hour,
                Address_Delivery_Windows: workingAddress.Address_Delivery_Windows,
            };

            const response = await fetch(url, {
                method: "PUT",
                headers,
                body: JSON.stringify(deliveryWindowData),
            });

            if (response.ok) {
                const updatedAddress = await response.json();
                uiContext.notifyFn(null, {
                    message: "delivery_window_updated_successfully",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                });
                onSaveSuccess(updatedAddress.address);
                closeModalFn();
            } else {
                throw new Error("Failed to update delivery window");
            }
        } catch (error) {
            uiContext.notifyFn(null, {
                message: "error_updating_delivery_window",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="DeliveryWindowsModal p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <OwTitle title="edit_delivery_window" tag="h4" leftIconName="fad fa-clock" />
                <OwButton className="btn-danger" centerIconClass="fas fa-times" fn={closeModalFn} />
            </div>

            <DeliveryWindowsSection address={workingAddress} onDeliveryWindowsChange={handleDeliveryWindowsChange} />

            <div className="mt-4 d-flex justify-content-end">
                <OwButton title="cancel" className="btn-secondary me-2" fn={closeModalFn} />
                <OwButton
                    title="save"
                    fn={handleSave}
                    disabled={isSaving}
                    leftIconClass={isSaving ? "fas fa-spinner fa-spin" : undefined}
                />
            </div>
        </div>
    );
};

export default DeliveryWindowsModal;
